import React from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

// Shared building blocks for the fleet detail pages (Single Vehicle, Single
// Asset) — flat headers and label/value rows instead of the old accordion +
// bubble-card look, plus the hero stat tiles and tab badges.

export const formatDate = (date) =>
  date ? moment(date).format('Do MMM YYYY') : null

// Traffic-light tone for an expiry date: past = error, within a month =
// warning, otherwise success. Null when there is no date to judge.
export const dateTone = (date) => {
  if (!date) return null
  if (moment(date).isBefore(moment())) return 'error'
  if (moment(date).isBefore(moment().add(1, 'months'))) return 'warning'
  return 'success'
}

// At-a-glance figure for the hero row. `tone` is a palette key (error,
// warning, success, anGreen…) rendered as a dot beside the value.
export const StatTile = ({ label, value, caption, tone }) => (
  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, minWidth: 0, flex: 1 }}>
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      {tone && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: `${tone}.main`,
            flexShrink: 0,
          }}
        />
      )}
      <Typography sx={{ fontWeight: 800, fontSize: '1rem' }} noWrap>
        {value || '—'}
      </Typography>
    </Stack>
    {caption && (
      <Typography
        component="div"
        variant="caption"
        color="text.secondary"
        noWrap
      >
        {caption}
      </Typography>
    )}
  </Card>
)

// Tab caption with an optional count badge (defects, attachments…).
export const TabLabel = ({ label, count, color }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <span>{label}</span>
    {count !== undefined && (
      <Chip
        size="small"
        label={count}
        color={color}
        sx={{ borderRadius: 1, height: 18, fontSize: '0.7rem' }}
      />
    )}
  </Stack>
)

export const SectionHeader = ({ title, caption, action, chip }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    flexWrap="wrap"
    useFlexGap
  >
    <Box minWidth={0}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {chip}
      </Stack>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
    {action && <Box flexShrink={0}>{action}</Box>}
  </Stack>
)

export const InfoRow = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    py={0.9}
  >
    <Typography variant="body2" color="text.secondary" flexShrink={0}>
      {label}
    </Typography>
    {React.isValidElement(value) ? (
      value
    ) : (
      <Typography
        variant="body2"
        fontWeight={600}
        textAlign="right"
        sx={{ overflowWrap: 'anywhere' }}
      >
        {value === undefined || value === null || value === '' ? '—' : value}
      </Typography>
    )}
  </Stack>
)

export const InfoList = ({ rows }) => (
  <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
    {rows.map((row) => (
      <InfoRow key={row.label} label={row.label} value={row.value} />
    ))}
  </Stack>
)
